import { MutableRefObject, useCallback, useRef } from 'react';
import { SecurityIssue } from '../api/types';

type QuiesceKeyed = Pick<SecurityIssue, 'tool' | 'src_id'>;

function issueKey(row: QuiesceKeyed): string {
  return `${row.tool}::${row.src_id}`;
}

/**
 * Backend query results are cached for up to 15 minutes and that cache is
 * per-pod (see security-insights-backend's `service/cache.ts`) — busting it
 * on quiesce/lift wouldn't reach the other replicas, so a refetch right
 * after a successful quiesce can still return the pre-quiesce row. This
 * tracks findings quiesced during the current session and filters them out
 * of already-fetched/refetched rows so the acting user's own view updates
 * immediately, independent of backend cache staleness. `unhide` is the
 * inverse, for pages that also support lifting a quiesce in-place.
 */
export function useOptimisticQuiesceHide() {
  const hiddenRef = useRef<Set<string>>(new Set());

  const hide = useCallback((row: QuiesceKeyed) => {
    hiddenRef.current.add(issueKey(row));
  }, []);

  const unhide = useCallback((row: QuiesceKeyed) => {
    hiddenRef.current.delete(issueKey(row));
  }, []);

  const filterHidden = useCallback(<T extends QuiesceKeyed>(rows: T[]): T[] => {
    if (hiddenRef.current.size === 0) return rows;
    return rows.filter(row => !hiddenRef.current.has(issueKey(row)));
  }, []);

  return { hide, unhide, filterHidden };
}

/**
 * Per-table accumulator threaded through `applyOptimisticHide` across every
 * page fetch for the current filter set. Recreate (don't mutate in place)
 * whenever filters change — see call sites.
 */
export interface HideAdjustment {
  /** Distinct keys of every session-hidden row seen across all page
   *  fetches so far (not just a per-page max) — two different findings
   *  quiesced this session can land on two different pages, and each fetch
   *  only ever sees one of them, so a per-page max would under-count. */
  hidden: Set<string>;
  /** Highest raw server `totalCount` seen so far for the current filter
   *  set — see `applyOptimisticHide` for how this bounds double-counting. */
  baselineTotal: number;
}

export function createHideAdjustment(): HideAdjustment {
  return { hidden: new Set<string>(), baselineTotal: 0 };
}

/**
 * Applies session-quiesced hiding to one server page of paginated results.
 * Skipped entirely when `includeQuiesced` is on — the user explicitly asked
 * to see quiesced findings, so hiding (and adjusting the total for it) would
 * make the toggle look broken.
 *
 * The backend's own `totalCount` can still include a just-quiesced row for
 * up to 15 minutes (see the module doc comment above), so naively
 * subtracting the full accumulated hidden-set size from every fetch would
 * double-subtract once the backend cache catches up and starts excluding
 * those rows itself. To avoid that, `adjustmentRef.current.baselineTotal`
 * tracks the highest raw `totalCount` observed so far for this filter set
 * (a high-water mark) — `baselineTotal - result.totalCount` is how much the
 * backend's own count has already dropped since that peak, from cache
 * catch-up or any other real removal. Subtracting that already-caught-up
 * amount from the accumulated hidden-set size leaves only the *still-stale*
 * portion still needing a client-side adjustment, which keeps the reported
 * total correct without re-subtracting rows the backend has already
 * dropped. Reset `adjustmentRef.current` (via `createHideAdjustment()`)
 * whenever the filters change.
 */
export function applyOptimisticHide<T extends QuiesceKeyed>(
  result: { data: T[]; totalCount: number },
  includeQuiesced: boolean | undefined,
  filterHidden: (rows: T[]) => T[],
  adjustmentRef: MutableRefObject<HideAdjustment>,
): { data: T[]; totalCount: number } {
  if (includeQuiesced) {
    return result;
  }
  const adjustment = adjustmentRef.current;
  const data = filterHidden(result.data);
  if (data.length !== result.data.length) {
    const visible = new Set(data);
    for (const row of result.data) {
      if (!visible.has(row)) {
        adjustment.hidden.add(issueKey(row));
      }
    }
  }
  adjustment.baselineTotal = Math.max(
    adjustment.baselineTotal,
    result.totalCount,
  );
  const backendCaughtUp = Math.max(
    0,
    adjustment.baselineTotal - result.totalCount,
  );
  const stillStale = Math.max(0, adjustment.hidden.size - backendCaughtUp);
  return {
    data,
    totalCount: Math.max(0, result.totalCount - stillStale),
  };
}
